// src/services/EntityService.js
import storage from './StorageAdapter';
import auditService from './AuditService';
import * as entitiesApi from '../api/entities';

const STORAGE_KEY = 'rms_entities';
const ACTIVE_ENTITY_KEY = 'rms_active_entity';

const ENTITY_TYPES = ['parent', 'subsidiary', 'branch', 'joint_venture'];

const INITIAL_ENTITIES = [
  {
    id: 'ent-001',
    name: 'Prime Financial Group AB',
    orgNumber: '556000-0001',
    type: 'parent',
    parentId: null,
    description: '',
    country: 'SE',
    created: '2025-01-15T08:00:00',
    updated: '2025-01-15T08:00:00'
  }
];

// Backend POST /api/entities kräver id matchande ^[a-zA-Z0-9_-]{1,64}$.
// Frontend genererar `ent-<ts>_<rand>` vilket matchar — men sanera ändå.
const VALID_ID_RE = /^[a-zA-Z0-9_-]{1,64}$/;

// Plocka ut backend-fält från frontend-entitet. Backend lagrar
// {id, name, orgNumber, parentId, metadata} — type/description/country/reportColors
// går i metadata-JSON.
function toBackendPayload(entity) {
  const { id, name, orgNumber, parentId, type, description, country, reportColors } = entity;
  return {
    id,
    name: name || '',
    orgNumber: orgNumber || null,
    parentId: parentId || null,
    metadata: {
      type: type || 'subsidiary',
      description: description || '',
      country: country || 'SE',
      ...(reportColors ? { reportColors } : {}),
    },
  };
}

// Konvertera backend-rad → frontend-entitet (metadata flattenad).
function fromBackendRow(row) {
  const md = row.metadata || {};
  return {
    id: row.id,
    name: row.name || '',
    orgNumber: row.orgNumber || '',
    type: md.type || 'subsidiary',
    parentId: row.parentId || null,
    description: md.description || '',
    country: md.country || 'SE',
    reportColors: md.reportColors || null,
    created: row.createdAt || new Date().toISOString(),
    updated: row.updatedAt || new Date().toISOString(),
  };
}

class EntityService {
  constructor() {
    this.listeners = [];
    this._ensureData();
  }

  _ensureData() {
    const existing = storage.getJSON(STORAGE_KEY);
    if (!existing) {
      storage.setJSON(STORAGE_KEY, INITIAL_ENTITIES);
    }
  }

  subscribe(fn) {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter(l => l !== fn);
    };
  }

  _notify() {
    this.listeners.forEach(fn => fn());
  }

  getEntities() {
    return storage.getJSON(STORAGE_KEY, []);
  }

  getEntity(id) {
    return this.getEntities().find(e => e.id === id) || null;
  }

  getRootEntities() {
    return this.getEntities().filter(e => !e.parentId);
  }

  getChildren(parentId) {
    return this.getEntities().filter(e => e.parentId === parentId);
  }

  getTree() {
    const entities = this.getEntities();
    const buildNode = (entity) => ({
      ...entity,
      children: entities
        .filter(e => e.parentId === entity.id)
        .map(buildNode)
    });
    return entities
      .filter(e => !e.parentId)
      .map(buildNode);
  }

  getEntityPath(id) {
    const entities = this.getEntities();
    const path = [];
    let current = entities.find(e => e.id === id);
    while (current) {
      path.unshift(current);
      const pid = current.parentId;
      current = pid ? entities.find(e => e.id === pid) : null;
    }
    return path;
  }

  async addEntity(data) {
    const entities = this.getEntities();
    const now = new Date().toISOString();
    const newEntity = {
      id: 'ent-' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
      name: data.name || '',
      orgNumber: data.orgNumber || '',
      type: data.type || 'subsidiary',
      parentId: data.parentId || null,
      description: data.description || '',
      country: data.country || 'SE',
      reportColors: data.reportColors || null,
      created: now,
      updated: now
    };
    entities.push(newEntity);
    await storage.setJSON(STORAGE_KEY, entities);
    auditService.logCreate('Admin', 'entity', newEntity.id, { name: newEntity.name });
    this._notify();
    // INFRA-112 dual-write: synca till backend fire-and-forget.
    this._syncEntityToBackend('create', newEntity).catch((err) => {
      console.warn('[EntityService] backend-sync create failed:', err?.message);
    });
    return newEntity;
  }

  async updateEntity(id, data) {
    const entities = this.getEntities();
    const idx = entities.findIndex(e => e.id === id);
    if (idx === -1) return null;

    // Prevent setting parent to self or own descendant
    if (data.parentId) {
      if (data.parentId === id) return null;
      const descendants = this._getDescendantIds(id, entities);
      if (descendants.includes(data.parentId)) return null;
    }

    entities[idx] = {
      ...entities[idx],
      ...data,
      id,
      updated: new Date().toISOString()
    };
    await storage.setJSON(STORAGE_KEY, entities);
    auditService.logUpdate('Admin', 'entity', id, data, `Entity updated: ${entities[idx].name}`);
    this._notify();
    this._syncEntityToBackend('update', entities[idx]).catch((err) => {
      console.warn('[EntityService] backend-sync update failed:', err?.message);
    });
    return entities[idx];
  }

  async deleteEntity(id) {
    const entities = this.getEntities();
    const entity = entities.find(e => e.id === id);
    if (!entity) return false;

    // Re-parent children to deleted entity's parent
    const children = entities.filter(e => e.parentId === id);
    children.forEach(child => {
      child.parentId = entity.parentId;
    });

    const filtered = entities.filter(e => e.id !== id);
    await storage.setJSON(STORAGE_KEY, filtered);
    auditService.logDelete('Admin', 'entity', id);
    this._notify();
    this._syncEntityToBackend('delete', entity).catch((err) => {
      console.warn('[EntityService] backend-sync delete failed:', err?.message);
    });
    return true;
  }

  getDescendantIds(id) {
    return this._getDescendantIds(id, this.getEntities());
  }

  _getDescendantIds(parentId, entities) {
    const result = [];
    const children = entities.filter(e => e.parentId === parentId);
    children.forEach(child => {
      result.push(child.id);
      result.push(...this._getDescendantIds(child.id, entities));
    });
    return result;
  }

  getActiveEntityId() {
    const id = storage.getItem(ACTIVE_ENTITY_KEY);
    if (id && this.getEntity(id)) return id;
    // Fallback to first root entity
    const roots = this.getRootEntities();
    const fallback = roots.length > 0 ? roots[0].id : null;
    if (fallback) storage.setItem(ACTIVE_ENTITY_KEY, fallback);
    return fallback;
  }

  getActiveEntity() {
    const id = this.getActiveEntityId();
    return id ? this.getEntity(id) : null;
  }

  async setActiveEntityId(id) {
    const entity = this.getEntity(id);
    if (!entity) return false;
    await storage.setItem(ACTIVE_ENTITY_KEY, id);
    window.dispatchEvent(new CustomEvent('rms_active_entity_changed', {
      detail: { entityId: id, entity }
    }));
    this._notify();
    return true;
  }

  getEntityReportColors(entityId) {
    const entity = this.getEntity(entityId);
    if (!entity) return null;
    const rc = entity.reportColors;
    if (!rc) return null;
    // Return only if at least one color is set
    if (rc.primary || rc.secondary || rc.accent) return rc;
    return null;
  }

  getEntityCount() {
    return this.getEntities().length;
  }

  async reset() {
    await storage.setJSON(STORAGE_KEY, INITIAL_ENTITIES);
    await storage.removeItem(ACTIVE_ENTITY_KEY);
    this._notify();
  }

  // ------------------------------------------------------------------
  // ADM-160 / INFRA-112 — backend dual-write för entities.
  //
  // Strategi: localStorage förblir snabb läs-cache. Backend rms_entities är
  // auktoritativ källa över tid. addEntity/updateEntity/deleteEntity
  // dual-writar fire-and-forget. App.js initFromBackend pullar vid login.
  // ------------------------------------------------------------------

  async _syncEntityToBackend(op, entity) {
    if (!entity || !entity.id) return;
    if (!VALID_ID_RE.test(entity.id)) {
      console.warn('[EntityService] entity-id passar inte backend-regex:', entity.id);
      return;
    }
    if (op === 'delete') {
      try {
        await entitiesApi.deleteEntity(entity.id);
      } catch (err) {
        if (err?.status !== 404) throw err;
      }
      return;
    }

    const payload = toBackendPayload(entity);
    // Försök update först (vanlig path); 404 → create.
    let exists = false;
    try {
      await entitiesApi.getEntity(entity.id);
      exists = true;
    } catch (err) {
      if (err?.status !== 404) throw err;
    }

    if (exists) {
      const { id, ...patch } = payload;
      await entitiesApi.updateEntity(id, patch);
    } else {
      try {
        await entitiesApi.createEntity(payload);
      } catch (err) {
        // Race: någon annan skapade samtidigt → fall tillbaka till update.
        if (err?.status === 400 && /finns redan/i.test(err?.message || '')) {
          const { id, ...patch } = payload;
          await entitiesApi.updateEntity(id, patch);
        } else {
          throw err;
        }
      }
    }
  }

  /**
   * Pulla entiteter från backend. Strategi (ADM-165):
   *   - Backend tom → behåll lokal (med fallback-seed) och pusha upp lokala som
   *     dual-write fire-and-forget (offline-first / fresh-deploy-stöd).
   *   - Backend har data → backend vinner TOTALT. Lokala-only entries
   *     (hårdkodad seed `Prime Financial Group AB`, ghost-data från legacy
   *     localStorage, eller orphans efter manuell DB-städning) skrivs över.
   *     Vi behåller bara `created`-tidstämpel om id matchar.
   *
   * Kallas från App.js efter login.
   */
  async initFromBackend() {
    try {
      const backendList = await entitiesApi.listEntities();
      if (!Array.isArray(backendList)) return;

      const localEntities = this.getEntities();

      if (backendList.length === 0) {
        // Backend tom — pusha lokala upp men rör inte localStorage.
        // App.js fallback-grenen tar hand om "Default Organization"-seed
        // om både backend och local är tom efter detta.
        for (const e of localEntities) {
          try { await this._syncEntityToBackend('create', e); } catch (_) {}
        }
        return;
      }

      // Backend har data → backend vinner totalt. Bygg listan från backend
      // (inga lokala-only entries). Detta ELIMINERAR ghost-entities som
      // hårdkodade seeds eller orphans i localStorage från tidigare versioner.
      const localById = new Map(localEntities.map(e => [e.id, e]));
      const replacement = backendList.map(b => {
        const local = localById.get(b.id);
        const fromBackend = fromBackendRow(b);
        return {
          ...fromBackend,
          // Behåll bara `created` från local om id matchar — annars använd
          // backend createdAt. Inget annat fält tas från local.
          created: local?.created || fromBackend.created,
        };
      });

      await storage.setJSON(STORAGE_KEY, replacement);

      // Aktivera entity som inte längre finns? Ändra till första root.
      const activeId = storage.getItem(ACTIVE_ENTITY_KEY);
      if (activeId && !replacement.some(e => e.id === activeId)) {
        const newActive = replacement.find(e => !e.parentId)?.id || replacement[0]?.id || null;
        if (newActive) storage.setItem(ACTIVE_ENTITY_KEY, newActive);
      }

      this._notify();
    } catch (err) {
      console.debug('[EntityService] initFromBackend skipped:', err?.message);
    }
  }
}

const entityService = new EntityService();
export { ENTITY_TYPES };
export default entityService;
